/**
 * Immune units (M36, docs/RARES.md; Kevin 2026-10-10: "occasional units immune to certain types of damage to force
 * rotation of towers"). An enemy's `immunity` is a bitmask of `Immunity` (data/spawnRules.ts): damage of an immune type
 * does nothing (no hit, no shred, no burn), and an immune-to-slow unit shrugs off slows (stuns, freezes, roots and
 * Polymorph still take). Which spawns are immune is decided with the schedule (`markImmune`), so the wave preview
 * shows them. The `EnemyImmune` event is presentation only (an "IMMUNE" popup), at most once per enemy per tower per second.
 */
import { BalanceData, DamageType } from "../data/balance";
import { Immunity, ImmunityKind } from "../data/spawnRules";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { SimConstants } from "./constants";
import { Enemy, Tower } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { SpawnEntry } from "./waveSchedule";

/** Tags the per-stage immunity draw apart from the run's own RNG and every other seeded stream. */
const IMMUNE_TAG = "immuneStage";

/** The enemy's immunity bitmask (`Immunity`; 0 = none). */
export function immunityOf(e: Enemy): number { return e.immunity; }

/** The immunity bit that blocks damage of `type`: physical, magic, or fire for burn. */
export function immunityForDamage(type: DamageType): ImmunityKind {
  return type === DamageType.Physical ? Immunity.Physical : type === DamageType.Magic ? Immunity.Magic : Immunity.Fire;
}

/** Whether `e` takes no damage of `type`. */
export function immuneToDamage(e: Enemy, type: DamageType): boolean { return (e.immunity & immunityForDamage(type)) !== 0; }

/** Whether slows (Frost hits, slow auras and zones, Deep Freeze) pass `e` by. */
export function immuneToSlow(e: Enemy): boolean { return (e.immunity & Immunity.Slow) !== 0; }

/**
 * Tells the view an attack did nothing (EnemyImmune: a = enemy id, b = tower id or 0, c = the Immunity bit), at most
 * once per enemy per tower per second. Never part of the hash: the notes only pace an event.
 */
export function noteImmune(sim: LevelSim, e: Enemy, source: Tower | null, kind: ImmunityKind): void {
  const towerId = source?.id ?? 0, notes = e.immuneNotes;
  for (let i = 0; i < notes.length; i += 2) {
    if (notes[i] !== towerId) continue;
    if (sim.tick - notes[i + 1] < SimConstants.TicksPerSecond) return;
    notes[i + 1] = sim.tick;
    sim.events.add(SimEventKind.EnemyImmune, e.id, towerId, kind);
    return;
  }
  notes.push(towerId, sim.tick);
  sim.events.add(SimEventKind.EnemyImmune, e.id, towerId, kind);
}

function stageRng(seed: bigint, stage: number): Pcg32 {
  const h = new Fnv1a64();
  h.writeString(IMMUNE_TAG);
  h.writeLong(seed);
  h.writeInt(stage);
  return new Pcg32(h.value);
}

/**
 * Marks a plain stage's immune spawns (enemies.json `immunity`): from `fromLevel` and `fromStage`, one stage in about
 * `everyStages` (a draw from the run's seed and the stage, never the run's RNG) picks one kind by weight and one of the
 * stage's archetypes that has at least `countMin` ordinary spawns (none: no immune units that stage), and makes `countMin`–`countMax` of that archetype's ordinary spawns immune, spread evenly over
 * them, at `hpMult` of their HP. Bosses, rares and elites never are. Pure: the preview and the run mark the same spawns.
 */
export function markImmune(balance: BalanceData, levelNumber: number, stage: number, entries: SpawnEntry[], seed: bigint): SpawnEntry[] {
  const r = balance.spawnRules.immunity;
  if (levelNumber < r.fromLevel || stage < r.fromStage || r.countMax <= 0) return entries;
  const rng = stageRng(seed, stage);
  if (rng.nextInt(r.everyStages) !== 0) return entries;
  const total = r.kindWeights.reduce((a, w) => a + w, 0);
  if (total <= 0) return entries;
  let pick = rng.nextInt(total), bit = 0;
  for (let i = 0; i < r.kindWeights.length; i++) { pick -= r.kindWeights[i]; if (pick < 0) { bit = 1 << i; break; } }
  const open = (e: SpawnEntry): boolean => !e.isBoss && (e.affix ?? -1) < 0 && balance.enemies[e.archetype].rareChapter === 0 && !balance.enemies[e.archetype].legend;
  // only an archetype with at least `countMin` ordinary spawns this stage: "a few" immune units, never a lone one
  const count = new Map<number, number>();
  for (const e of entries) if (open(e)) count.set(e.archetype, (count.get(e.archetype) ?? 0) + 1);
  const archetypes: number[] = [];
  for (const e of entries) if (open(e) && !archetypes.includes(e.archetype) && count.get(e.archetype)! >= Math.max(1, r.countMin)) archetypes.push(e.archetype);
  if (archetypes.length === 0) return entries;
  const archetype = archetypes[rng.nextInt(archetypes.length)];
  const slots: number[] = [];
  entries.forEach((e, i) => { if (open(e) && e.archetype === archetype) slots.push(i); });
  const n = Math.min(slots.length, r.countMin + rng.nextInt(r.countMax - r.countMin + 1));
  const out = [...entries];
  for (let k = 0; k < n; k++) {
    const i = slots[Math.floor(((2 * k + 1) * slots.length) / (2 * n))];
    out[i] = { ...out[i], immunity: bit, hpMult: r.hpMult.eq(Fix64.One) ? out[i].hpMult : out[i].hpMult.mul(r.hpMult) };
  }
  return out;
}
